// The user can load money onto their wallet on this page.  They have three choices:
// 1. Etransfer, 2. Cash, 3. Credit Card.  They will then set the amount they want to load
// and click next to confirm.
import React, { useEffect } from "react";
import Button from "@material-ui/core/Button";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import TextField from "@material-ui/core/TextField";
import FormLabel from "@material-ui/core/FormLabel";
import Typography from "@material-ui/core/Typography";

export default function LoadWalletDialog({
  open,
  amount,
  setAmount,
  onClose,
  defaultAmount = "",
  balance = 0,
  setEtransferConfirmOpen,
  setCashConfirmOpen,
  setCreditCardConfirmOpen,
  communityLocation,
  paymentMethod,
  setPaymentMethod,
  placingOrder = false,
  CloverCreditCardPercentage,
  CloverCreditCardFlatFee,
}) {
  // Checks to see if the user is using a communityLocation or a distributionLocation and checks what the
  // etransfer and cash transaction fees are and then sets them.
  const distributionLocation = communityLocation.pickupLocation
    ? communityLocation.pickupLocation
    : { ...communityLocation };
  const cashTransactionFee = distributionLocation.cashTransactionFee ?? 0;
  const etransferTransactionFee =
    distributionLocation.etransferTransactionFee ?? 0;

  // Round the values up to cents
  const toCents = (num) => Math.round(parseFloat(num) * 100);

  // Whether the user's current wallet balance is short of the order total.
  // When true we show a warning banner with the breakdown so they understand
  // why they're being asked to load funds.
  const totalFloat = parseFloat(defaultAmount) || 0;
  const balanceFloat = parseFloat(balance) || 0;
  const shortfall = Math.max(totalFloat - balanceFloat, 0);
  const showInsufficientFundsWarning = totalFloat > 0 && shortfall > 0;

  // On open set the defaults.
  useEffect(() => {
    if (open) {
      // The default is the etransfer method.
      setPaymentMethod("etransfer");
      // Sets the default amount and makes sure it's either 0 or a number.
      const defaultFloat = parseFloat(defaultAmount) || 0; // fallback to 0 if undefined
      const balanceFloat = parseFloat(balance) || 0;
      // The amount needed to load is the total minus their current wallet balance.
      // A negative balance (money owed) effectively increases how much they need
      // to load, while a positive balance reduces it. Never prefill below 0.
      const neededAmount = Math.max(defaultFloat - balanceFloat, 0);
      // Ensures no more than 2 decimals on the default amount.
      setAmount(Math.ceil(neededAmount * 100) / 100);
    }
  }, [open, defaultAmount, balance]);

  // If the user cancels the wallet load set the payment method back to F2PMCredits.
  const handleCancel = () => {
    onClose(false);
    setPaymentMethod("F2PMCredits");
  };

  // If the user has selected their option and loaded enough money to cover their order they
  // can proceed to the confirmation.
  const handleNext = () => {
    onClose(false);
    if (paymentMethod === "etransfer") {
      setEtransferConfirmOpen(true);
    } else if (paymentMethod === "cash") {
      setCashConfirmOpen(true);
    } else {
      setCreditCardConfirmOpen(true);
    }
  };

  // If the user changes the amount update it while making sure it stays a number.
  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (value === "") {
      setAmount(""); // allow blank
    } else {
      const parsed = parseFloat(value);
      setAmount(isNaN(parsed) ? "" : parsed); // ✅ no clamping
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)}>
      <DialogTitle>Load Your Wallet</DialogTitle>

      <DialogContent>
        {showInsufficientFundsWarning && (
          <div
            style={{
              backgroundColor: "#FFF4E5",
              border: "1px solid #FFB74D",
              borderRadius: 8,
              padding: 12,
              marginBottom: 16,
            }}
          >
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Wallet balance does not cover your order total
            </Typography>
            <Typography variant="body2" style={{ marginTop: 8 }}>
              <strong>Order total:</strong> ${totalFloat.toFixed(2)}
              <br />
              <strong>Current balance:</strong> ${balanceFloat.toFixed(2)}
              <br />
              <strong>Amount needed:</strong> ${shortfall.toFixed(2)}
            </Typography>
            {placingOrder && (
              <Typography
                variant="body2"
                style={{ marginTop: 8, fontStyle: "italic" }}
              >
                Once you confirm your payment on the next step, your order will
                be placed automatically.
              </Typography>
            )}
          </div>
        )}

        <Typography variant="body1" gutterBottom>
          Your F2PM wallet holds the funds you use to pay for your orders. If an
          order is later changed or refunded, the money goes back into your
          wallet and can be used on future orders. Some payment methods include
          a small transaction fee each time you load money, while others are
          free — the fee (if any) is shown next to each option below.
        </Typography>

        <Typography variant="body2" style={{ fontStyle: "italic" }}>
          Tip: Loading enough to cover several weeks of orders means you don't
          need to send a payment every time you order, and can also save on
          transaction fees when they apply.
        </Typography>

        <br />

        {/* Payment Method */}
        <FormLabel component="legend">Choose Payment Method</FormLabel>
        <RadioGroup
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value)}
        >
          <FormControlLabel
            value="etransfer"
            control={<Radio />}
            label={`E-Transfer ($${etransferTransactionFee.toFixed(
              2,
            )} per transaction)`}
          />
          <FormControlLabel
            value="cash"
            control={<Radio />}
            label={
              communityLocation?.cashEnabled
                ? `Cash ($${cashTransactionFee.toFixed(2)} per transaction)`
                : `Cash ($${cashTransactionFee.toFixed(
                    2,
                  )} per transaction) - unavailable at this location`
            }
            disabled={!communityLocation?.cashEnabled}
          />
          <FormControlLabel
            value="creditcard"
            control={<Radio />}
            label={
              CloverCreditCardFlatFee > 0
                ? `Credit Card - ${CloverCreditCardPercentage}% per transaction + $${CloverCreditCardFlatFee.toFixed(
                    2,
                  )} CAD`
                : `Credit Card - ${CloverCreditCardPercentage}% per transaction`
            }
          />
        </RadioGroup>

        {/* Amount Input */}
        <TextField
          label="Amount to Load"
          type="number"
          value={amount}
          onChange={handleAmountChange}
          fullWidth
          margin="normal"
          inputProps={{ min: 0, step: "0.01" }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={() => handleCancel(false)} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleNext}
          color="primary"
          variant="contained"
          disabled={
            !amount ||
            isNaN(parseFloat(amount)) ||
            toCents(parseFloat(amount)) <
              Math.max(
                toCents(parseFloat(defaultAmount) || 0) -
                  toCents(parseFloat(balance) || 0),
                0,
              )
          }
        >
          Next
        </Button>
      </DialogActions>
    </Dialog>
  );
}
